/**
 * פאנל צד למידע על עיר + פעולות
 */

import { useState } from 'react';
import type { RoundPlayer } from '../../api/client.js';
import type { WorldState, City, Dyn } from '../RoundScreen.js';

export function CityPanel({
  city,
  state,
  player,
  playerDynasty,
  onClose,
  onIntent,
}: {
  city: City;
  state: WorldState;
  player: RoundPlayer;
  playerDynasty: Dyn | undefined;
  onClose: () => void;
  onIntent: (type: string, payload: Record<string, unknown>) => Promise<{ success: boolean; message?: string }>;
}) {
  const [message, setMessage] = useState<string | null>(null);

  // מי החזק בעיר?
  const cityFollowers = state.followers.filter((f) => f.cityId === city.id);
  cityFollowers.sort((a, b) => b.count - a.count);

  const isPlayerHome = city.id === player.home_city_id;
  const country = state.entities.countries[city.countryId];

  // מוסדות בעיר
  const institutions = Object.values(state.entities.institutions).filter(
    (i) => i.cityId === city.id && i.status === 'active',
  );

  // כמה זהב יש לנו
  const playerGold = playerDynasty?.resources.gold ?? 0;

  const handleSendEmissary = async (): Promise<void> => {
    if (playerGold < 500) {
      setMessage('אין מספיק זהב (נדרש 500)');
      return;
    }
    const result = await onIntent('SEND_EMISSARY', {
      targetCityId: city.id,
      charisma: 70,
      languagesFocus: playerDynasty?.ethnicity === 'ashkenazi' ? 'yiddish' : 'arabic',
    });
    if (result.success) {
      setMessage('שלוח נשלח בהצלחה');
    } else {
      setMessage(result.message ?? 'שגיאה');
    }
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <aside className="city-panel">
      <div className="city-panel-header">
        <button className="close" onClick={onClose}>✕</button>
        <h2>{city.name}</h2>
        <div className="meta">
          {country?.name ?? '?'}
          {city.isHolyPlace && <span className="badge holy">🕯 מקום קדוש</span>}
          {city.hasChareidiMunicipality && <span className="badge mun">🏛 עירייה חרדית</span>}
          {isPlayerHome && <span className="badge home">🏠 העיר שלך</span>}
        </div>
      </div>

      <div className="city-section">
        <h3>אוכלוסייה</h3>
        <div className="stat-row">
          <span>יהודים בעיר</span>
          <strong>{city.jewishPopulation.toLocaleString('he-IL')}</strong>
        </div>
        <div className="stat-row">
          <span>סה"כ תושבים</span>
          <strong>{city.totalPopulation.toLocaleString('he-IL')}</strong>
        </div>
        <div className="stat-row">
          <span>גודל</span>
          <strong>{TIER_LABELS[city.sizeTier] ?? city.sizeTier}</strong>
        </div>
      </div>

      <div className="city-section">
        <h3>חצרות שולטות</h3>
        {cityFollowers.length === 0 ? (
          <div className="empty">אף אחת</div>
        ) : (
          <ul className="rulers-list">
            {cityFollowers.slice(0, 5).map((f) => {
              const d = state.entities.dynasties[f.dynastyId];
              if (!d) return null;
              const isMe = d.id === player.dynasty_id;
              return (
                <li key={f.dynastyId} className={isMe ? 'me' : ''}>
                  <span className="dyn-name">
                    {d.name}
                    {isMe && ' (אתה)'}
                  </span>
                  <span className="dyn-count">{f.count.toLocaleString('he-IL')} חסידים</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {institutions.length > 0 && (
        <div className="city-section">
          <h3>מוסדות ({institutions.length})</h3>
          <ul className="inst-list">
            {institutions.slice(0, 8).map((i) => (
              <li key={i.id}>
                <span>{INST_LABELS[i.type] ?? i.type}</span>
                <span className="level">רמה {i.level}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="city-section actions">
        <h3>פעולות</h3>
        {!isPlayerHome ? (
          <>
            <button
              className="btn-action"
              disabled={playerGold < 500}
              onClick={() => void handleSendEmissary()}
            >
              🎯 שלח שלוח לגייס חסידים
              <span className="cost">💰 500</span>
            </button>
            <div className="help">שלוח מתחיל לגייס חסידים בעיר הזאת. יבוא לוקח זמן.</div>
          </>
        ) : (
          <div className="help">זו העיר שלך — בנייה נעשית ב"החצר שלי"</div>
        )}
        {message && <div className="msg">{message}</div>}
      </div>
    </aside>
  );
}

const TIER_LABELS: Record<string, string> = {
  village: 'כפר',
  town: 'עיר קטנה',
  city: 'עיר',
  metropolis: 'מטרופולין',
};

const INST_LABELS: Record<string, string> = {
  yeshiva_ketana: 'ישיבה קטנה',
  yeshiva_gedola: 'ישיבה גדולה',
  kollel: 'כולל',
  beit_midrash: 'בית מדרש',
  talmud_torah: 'תלמוד תורה',
  beit_din: 'בית דין',
  mikvah: 'מקווה',
};
